// Subscription / entitlement abstraction. Credit-pack model:
//   * Every user starts with 2 free preview credits (LIFETIME, not per month).
//   * Paid packs top up `paidCredits` (3-pack $4.99, 10-pack $9.99).
//   * Lifetime pack ($49) tops up `lifetimeCredits` — capped at 100 previews,
//     never advertised as "unlimited".
//
// Credit accounting is server-authoritative (MongoDB via /api/usage and the
// atomic reserve/refund inside /api/preview/generate). This module keeps a
// small local cache so UI can render synchronously.
import { apiFetch, getClientId } from "@/lib/api";

export const PLAN = { FREE: "FREE", PRO: "PRO", TRIAL: "TRIAL" };

// Frontend "plan" tokens the paywall sends to /api/checkout/whop/session.
// Each maps server-side to a Whop plan id via `_resolve_plan_id`.
export const CHECKOUT_PLAN = {
  PACK3: "pack3",
  PACK10: "pack10",
  LIFETIME: "lifetime",
  // "monthly" (legacy $19.99 subscription) is retained server-side but
  // deliberately not surfaced in the new paywall or landing page.
};

// The three credit packs shown in the paywall + landing page. Copy is exactly
// what marketing requested — no "unlimited" language anywhere.
export const CREDIT_PACKS = [
  {
    id: "pack3",
    label: "3 AI Preview Pack",
    price: "$4.99",
    currency: "USD",
    credits: 3,
    description: "Try 3 different grooming styles.",
    ctaLabel: "Get 3 Previews",
    highlight: null,
  },
  {
    id: "pack10",
    label: "10 AI Preview Pack",
    price: "$9.99",
    currency: "USD",
    credits: 10,
    description: "Compare more styles before your dog's next groom.",
    ctaLabel: "Get 10 Previews",
    highlight: "Most Popular",
  },
  {
    id: "lifetime",
    label: "100 AI Preview Pack",
    price: "$49",
    currency: "USD",
    credits: 100,
    description: "Best value for repeat styling decisions.",
    ctaLabel: "Get 100 Previews",
    // Secondary emphasis — subtler than the coral "Most Popular" badge on
    // pack10 so the recommended plan still leads visually.
    highlight: "Best Value",
    savings: "Best price per preview",
  },
];

export const PLANS = {
  [PLAN.FREE]: {
    id: PLAN.FREE,
    label: "Free",
    price: "$0",
    period: "forever",
    // Legacy field name — freeAllowed is the source of truth after refresh.
    previewsPerCycle: 2,
    cycleDays: null,
    features: [
      "2 free AI grooming previews",
      "Core grooming styles",
      "Before / after comparison",
      "Downloadable Groomer Card",
    ],
    cta: "Current plan",
  },
  // Legacy PRO record — kept so any pre-existing subscriber's UI still
  // renders the label. New users never see this in the paywall.
  [PLAN.PRO]: {
    id: PLAN.PRO,
    label: "GroomPreview Pro",
    price: "$19.99",
    period: "month",
    currency: "USD",
    tagline: "Legacy Pro subscription.",
    previewsPerCycle: 999999,
    cycleDays: 30,
    features: [],
    cta: "Manage subscription",
  },
};

const USAGE_CACHE_KEY = "dogstyle_usage_v2";

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}
function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota */
  }
}

function emptyUsage() {
  return {
    plan: PLAN.FREE,
    freeAllowed: 2,
    freeUsed: 0,
    freeRemaining: 2,
    paidCredits: 0,
    lifetimeCredits: 0,
    creditsAvailable: 2,
    previewsUsed: 0,
    remaining: 2,
    // legacy back-compat fields (some old components still read these)
    previewsPerCycle: 2,
    cycleDays: null,
    cycleStartAt: null,
    lastGenerationAt: null,
    unlimited: false,
    synced: false,
  };
}

// ---- Plan ----------------------------------------------------------------
export function getSubscriptionStatus() {
  const cached = readJSON(USAGE_CACHE_KEY, null);
  const plan = cached?.plan || PLAN.FREE;
  const cfg = PLANS[plan] || PLANS[PLAN.FREE];
  return {
    plan,
    isPro: plan === PLAN.PRO,
    isTrial: plan === PLAN.TRIAL,
    isFree: plan === PLAN.FREE,
    previewsPerCycle: cached?.freeAllowed ?? cached?.previewsPerCycle ?? cfg.previewsPerCycle,
    cycleDays: cached?.cycleDays ?? cfg.cycleDays,
    label: cfg.label,
    features: cfg.features,
  };
}

// ---- Usage ---------------------------------------------------------------
export function getUsage() {
  return readJSON(USAGE_CACHE_KEY, null) || emptyUsage();
}

export function applyServerUsage(usage) {
  if (!usage) return getUsage();
  const next = { ...usage, synced: true, syncedAt: Date.now() };
  writeJSON(USAGE_CACHE_KEY, next);
  return next;
}

export async function refreshUsage() {
  try {
    const usage = await apiFetch(`/usage/${getClientId()}`, { timeoutMs: 15000 });
    return applyServerUsage(usage);
  } catch {
    return getUsage();
  }
}

export function canGeneratePreview() {
  const u = getUsage();
  // Total credits across all three buckets (free + paid + lifetime).
  const total =
    (u.freeRemaining ?? Math.max(0, (u.freeAllowed ?? 2) - (u.freeUsed ?? 0))) +
    (u.paidCredits ?? 0) +
    (u.lifetimeCredits ?? 0);
  if (u.synced && total <= 0) {
    return { allowed: false, remaining: 0, reason: "NO_CREDITS" };
  }
  return { allowed: true, remaining: total };
}

// Dev helper: forget cached usage (the server still remembers this device).
export function resetUsageCache() {
  try {
    localStorage.removeItem(USAGE_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

// ---- Pro waitlist --------------------------------------------------------
export async function joinWaitlist({ email, firstName = "", source = "unknown" }) {
  return apiFetch("/waitlist", {
    method: "POST",
    timeoutMs: 15000,
    body: {
      email: email.trim(),
      firstName: firstName.trim(),
      source,
      planInterest: PLAN.PRO,
      generationCount: getUsage().previewsUsed || 0,
      clientId: getClientId(),
    },
  });
}

// ---- Whop payments (Sandbox) --------------------------------------------
// Frontend never sees the API key or webhook secret. The backend creates a
// checkout session (a Whop "checkout configuration") carrying our clientId
// as metadata; we hand that session id to <WhopCheckoutEmbed sessionId=... />.

export async function fetchWhopConfig() {
  try {
    return await apiFetch("/checkout/whop/config", { timeoutMs: 10000 });
  } catch {
    return { configured: false, planId: null, environment: "sandbox" };
  }
}

export async function startProCheckout({ email, plan = "pack10" } = {}) {
  // `plan` accepts: "pack3" | "pack10" | "lifetime" | (legacy) "monthly".
  // The backend maps each to its configured Whop plan id.
  try {
    const res = await apiFetch("/checkout/whop/session", {
      method: "POST",
      timeoutMs: 15000,
      body: { clientId: getClientId(), email: email || undefined, plan },
    });
    if (!res?.configured) return { ok: false, reason: res?.reason || "NOT_CONFIGURED" };
    if (res?.ok === false) {
      return { ok: false, reason: res.error || "CHECKOUT_UNAVAILABLE", whopStatus: res.whopStatus, whopBody: res.whopBody, cause: res.cause, detail: res.detail };
    }
    return {
      ok: true,
      sessionId: res.sessionId,
      environment: res.environment || "sandbox",
      purchaseUrl: res.purchaseUrl,
    };
  } catch (err) {
    return { ok: false, reason: err?.status === 502 ? "CHECKOUT_UNAVAILABLE" : "ERROR" };
  }
}

// Stripe (BYOK test/live) — second payment option that runs alongside Whop.
// Frontend plan id → Stripe `lookup_key`.
const STRIPE_LOOKUP_KEYS = {
  pack3: "credits_pack_3",
  pack10: "credits_pack_10",
  lifetime: "credits_pack_100",
};

export async function startStripeCheckout({ plan = "pack10" } = {}) {
  const lookup_key = STRIPE_LOOKUP_KEYS[plan];
  if (!lookup_key) return { ok: false, reason: "UNKNOWN_PLAN" };
  try {
    const res = await apiFetch("/checkout/stripe/session", {
      method: "POST",
      timeoutMs: 20000,
      body: { clientId: getClientId(), lookup_key, origin_url: window.location.origin },
    });
    if (!res?.configured) return { ok: false, reason: res?.reason || "NOT_CONFIGURED" };
    if (!res.url) return { ok: false, reason: "NO_URL" };
    return { ok: true, sessionId: res.sessionId, url: res.url };
  } catch (err) {
    return {
      ok: false,
      reason: err?.status === 502 ? "CHECKOUT_UNAVAILABLE"
            : err?.status === 400 ? "BAD_REQUEST"
            : "ERROR",
    };
  }
}

export async function checkStripeStatus(sessionId) {
  try {
    return await apiFetch(`/checkout/stripe/status/${encodeURIComponent(sessionId)}`, { timeoutMs: 15000 });
  } catch {
    return { paymentStatus: "unknown" };
  }
}

// Verified Whop purchases attached to this device. Used by the paywall's
// Restore view to render a mini purchase history.
export async function fetchPurchases() {
  try {
    const res = await apiFetch(`/purchases/${getClientId()}`, { timeoutMs: 15000 });
    return { ok: true, purchases: res?.purchases || [] };
  } catch {
    return { ok: false, purchases: [] };
  }
}

// Email-based Pro restore is a two-step flow:
//   1) requestRestoreCode(email) → backend emails a one-time code IF Whop
//      confirms an active membership. The response is uniform whether or not
//      a membership exists — it never reveals subscription state.
//   2) verifyRestoreCode(email, code) → backend re-verifies with Whop, and
//      only after the correct code is entered from the same device does it
//      grant Pro to this clientId.
export async function requestRestoreCode(email) {
  try {
    return await apiFetch("/entitlements/restore/request", {
      method: "POST",
      timeoutMs: 20000,
      body: { clientId: getClientId(), email: (email || "").trim() },
    });
  } catch (err) {
    if (err?.status === 429) return { sent: false, reason: "TOO_MANY_REQUESTS" };
    return { sent: false, reason: "ERROR" };
  }
}

export async function verifyRestoreCode(email, code) {
  try {
    return await apiFetch("/entitlements/restore/verify", {
      method: "POST",
      timeoutMs: 20000,
      body: {
        clientId: getClientId(),
        email: (email || "").trim(),
        code: (code || "").trim(),
      },
    });
  } catch (err) {
    if (err?.status === 429) return { pro: false, reason: "TOO_MANY_REQUESTS" };
    return { pro: false, reason: "ERROR" };
  }
}
